/* sugar:assets/sticky-product.js@2.2.0 */
/**
 * Sticky Product Web Component
 * A fixed add-to-cart bar for product pages
 */
class StickyProduct extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.config = {
      position: 'bottom',
      visibility: 'after_button',
      buttonAction: 'add_to_cart',
      skipCart: false,
      scrollAnchorId: null,
      scrollOffset: 20,
      hideAtBottom: false,
      docked: false,
      showCurrencyCode: false,
      currencyCode: null,
      moneyFormat: '{{amount}}',
      productHandle: null
    };

    // State
    this.isVisible = false;
    this.currentVariantId = null;
    this.quantity = 1;
    this.isLoading = false;
    this.buyButtonObserver = null;
    this.pendingCheckout = false;
    this.buyButtonVisible = true; // Track if buy button is in viewport

    // Bound methods
    this.onScroll = this.onScroll.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onButtonClick = this.onButtonClick.bind(this);
    this.handleMainVariantChange = this.handleMainVariantChange.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleExternalQuantityChange = this.handleExternalQuantityChange.bind(this);
    this.handleMainFormQuantityChange = this.handleMainFormQuantityChange.bind(this);
    this.handleSubscriptionChange = this.handleSubscriptionChange.bind(this);
    this.handleOfferPricesUpdated = this.handleOfferPricesUpdated.bind(this);

    // Flag to prevent circular quantity updates
    this.isUpdatingQuantity = false;
    // Flag to track if initial quantity has been synced from offers
    this.initialQuantitySynced = false;
    // Subscription state
    this.currentSubscriptionType = 'onetime';
    this.currentSellingPlan = null;
    this.lastOfferEventSectionId = null;
    this.lastOfferEventBlockId = null;
  }

  connectedCallback() {
    this.parseConfig();
    this.cacheElements();
    this.initializeVariantId();
    this.setupEventListeners();
    this.setupExternalQuantityListeners();
    this.subscribeToEvents();
    this.initializeVisibility();
    this.setupBackdropWatch();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.removeExternalQuantityListeners();
    this.unsubscribeFromEvents();
  }

  // === INITIALIZATION ===

  parseConfig() {
    this.config.position = this.dataset.position || 'bottom';
    this.config.visibility = this.dataset.visibility || 'after_button';
    this.config.buttonAction = this.dataset.buttonAction || 'add_to_cart';
    this.config.skipCart = this.dataset.skipCart === 'true';
    this.config.scrollAnchorId = this.dataset.scrollAnchorId || null;
    this.config.scrollOffset = parseInt(this.dataset.scrollOffset) || 20;
    this.config.hideAtBottom = this.dataset.hideAtBottom === 'true';
    this.config.docked = this.dataset.docked === 'true';
    this.config.showCurrencyCode = this.dataset.showCurrencyCode === 'true';
    this.config.currencyCode = this.dataset.currencyCode || window.Shopify?.currency?.active || null;
    this.config.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.config.productHandle = (this.dataset.productHandle || '').trim() || null;
    this.sectionId = this.dataset.sectionId;
  }

  cacheElements() {
    this.button = this.querySelector('[data-sticky-button]');
    this.variantSelect = this.querySelector('[data-sticky-variant-select]');
    this.quantityInput = this.querySelector('[data-sticky-quantity-input]');
    this.quantitySelect = this.querySelector('[data-sticky-quantity-select]');
    this.quantityMinusBtn = this.querySelector('[data-sticky-quantity-minus]');
    this.quantityPlusBtn = this.querySelector('[data-sticky-quantity-plus]');
    this.variantImage = this.querySelector('[data-variant-image]');
    this.buttonText = this.querySelector('[data-button-text]');
    this.buttonTextOriginalHtml = this.buttonText?.innerHTML || this.buttonText?.dataset?.originalText || '';
    this.cacheButtonPriceElements();

    // Info price elements (for quantity-responsive pricing)
    this.infoPriceContainer = this.querySelector('[data-sticky-info-price]');
    this.infoCurrentPriceElement = this.querySelector('[data-sticky-info-current-price]');
    this.infoComparePriceElement = this.querySelector('[data-sticky-info-compare-price]');

    // Initialize base prices from data attributes (info price)
    if (this.infoPriceContainer) {
      this.basePrice = parseInt(this.infoPriceContainer.dataset.basePrice) || 0;
      this.baseComparePrice = parseInt(this.infoPriceContainer.dataset.baseComparePrice) || 0;
    }

    // Initialize base prices for button price
    if (this.priceElement) {
      this.buttonBasePrice = parseInt(this.priceElement.dataset.basePrice) || 0;
      this.buttonBaseComparePrice = parseInt(this.priceElement.dataset.baseComparePrice) || 0;
    }
  }

  cacheButtonPriceElements() {
    this.priceElements = Array.from(this.querySelectorAll('[data-sticky-price]'));
    this.currentPriceElements = Array.from(this.querySelectorAll('[data-sticky-current-price]'));
    this.comparePriceElements = Array.from(this.querySelectorAll('[data-sticky-compare-price]'));

    this.priceElement = this.priceElements[0] || null;
    this.currentPriceElement = this.currentPriceElements[0] || null;
    this.comparePriceElement = this.comparePriceElements[0] || null;
  }

  initializeVariantId() {
    // Try to get variant from sticky product's select
    if (this.variantSelect) {
      this.currentVariantId = this.variantSelect.value;
    } else {
      // Get from main product form
      const selector = 'product-form input[name="id"], form[action*="/cart/add"] input[name="id"]';
      const host = this.mainProductHost();
      const mainVariantInput = (host && host.querySelector(selector)) || document.querySelector(selector);
      if (mainVariantInput) {
        this.currentVariantId = mainVariantInput.value;
      }
    }
  }

  setupEventListeners() {
    // Scroll listener for hide at bottom
    if (this.config.hideAtBottom) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    // Theme editor: a re-rendered section brings a new buy button to watch
    this.onSectionLoad = () => this.rewatchBuyButton();
    document.addEventListener('shopify:section:load', this.onSectionLoad);

    // Variant selector
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.onVariantChange);
    }

    // Quantity input
    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', this.onQuantityChange);
    }

    // Quantity select (dropdown)
    if (this.quantitySelect) {
      this.quantitySelect.addEventListener('change', this.onQuantityChange);
    }

    // Quantity plus/minus buttons
    if (this.quantityMinusBtn) {
      this.quantityMinusBtn.addEventListener('click', () => this.adjustQuantity(-1));
    }
    if (this.quantityPlusBtn) {
      this.quantityPlusBtn.addEventListener('click', () => this.adjustQuantity(1));
    }

    // Button click
    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick);
    }
  }

  removeEventListeners() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.onBackdropScroll) {
      window.removeEventListener('scroll', this.onBackdropScroll);
      window.removeEventListener('resize', this.onBackdropResize);
    }
    if (this.onDockedScroll) {
      window.removeEventListener('scroll', this.onDockedScroll);
    }
    if (this.onDockedResize) {
      window.removeEventListener('resize', this.onDockedResize);
    }
    if (this.dockedPlaceholder && this.dockedPlaceholder.parentNode) {
      this.dockedPlaceholder.parentNode.removeChild(this.dockedPlaceholder);
    }
    if (this.variantSelect) {
      this.variantSelect.removeEventListener('change', this.onVariantChange);
    }
    if (this.quantityInput) {
      this.quantityInput.removeEventListener('change', this.onQuantityChange);
    }
    if (this.quantitySelect) {
      this.quantitySelect.removeEventListener('change', this.onQuantityChange);
    }
    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClick);
    }
    if (this.buyButtonObserver) {
      this.buyButtonObserver.disconnect();
    }
    if (this.onSectionLoad) {
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
    }
  }

  /*
   * The page's quantity field, resolved on demand and never trusted once held. It lives in the
   * product section, which the theme editor re-renders by replacing the element — a captured
   * reference then reads and writes a node that is no longer on the page, so the sticky bar
   * stops seeing quantity changes and stops driving them.
   */
  get mainQuantityField() {
    if (this._mainQty && this._mainQty.isConnected) return this._mainQty;

    this._mainQty =
      document.querySelector(
        'quantity-input input[name="quantity"], ' +
        '.product-form__quantity input[name="quantity"], ' +
        '.product-form__input input[name="quantity"], ' +
        'product-form input[name="quantity"], ' +
        'form[action*="/cart/add"] input[name="quantity"], ' +
        '.quantity__input[name="quantity"]'
      ) ||
      document.querySelector(
        '.quantity--dropdown select[name="quantity"], ' +
        'product-form select[name="quantity"], ' +
        'form[action*="/cart/add"] select[name="quantity"]'
      ) ||
      document
        .querySelector('.shopify-section-main-product, [data-section-type="product"]')
        ?.querySelector('[name="quantity"]') ||
      null;

    return this._mainQty;
  }

  setupExternalQuantityListeners() {
    // Listen for Offers block quantity changes
    document.addEventListener('offers:quantity-changed', this.handleExternalQuantityChange);

    // Listen for Subscriptions block type changes
    document.addEventListener('subscription:type-changed', this.handleSubscriptionChange);
    document.addEventListener('offers:prices-updated', this.handleOfferPricesUpdated);

    // Delegated: the field it watches can be replaced under it at any time.
    document.addEventListener('change', this.handleMainFormQuantityChange);

    // The offers controller sets the preselected offer quantity as it boots, so
    // wait for that before reading the value.
    this.scheduleInitialQuantitySync();
  }

  scheduleInitialQuantitySync() {
    // The offers block announces itself once its controller has picked the initial tier.
    this.onOffersReady = () => this.syncInitialQuantity();
    document.addEventListener('offers:ready', this.onOffersReady);

    // Already booted (or no offers block on the page): read what is there now.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.syncInitialQuantity(), { once: true });
    } else {
      this.syncInitialQuantity();
    }
  }

  syncInitialQuantity() {
    // A plan announced before the bar was on the page (a Silent Subscription seeds it as the
    // page loads) is read from the state the subscription blocks keep.
    if (!this.subscriptionStateKnown && window.subscriptionState) {
      this.subscriptionStateKnown = true;
      const state = window.subscriptionState;
      this.currentSubscriptionType = state.type || 'onetime';
      this.currentSellingPlan = state.sellingPlan || null;
      this.currentCompareBasis = state.compareBasis || 'onetime';
      if (this.currentSubscriptionType === 'subscribe') {
        this.updateInfoPriceForQuantity();
        this.updateButtonPriceForQuantity();
      }
    }

    // A tier can change the price at one unit too (a bundle variant, a discount, a plan the
    // page opens on), so its numbers are painted whatever the quantity turns out to be.
    if (document.querySelector('.offer.is-selected[data-computed-total-price]')) {
      this.updateInfoPriceForQuantity();
      this.updateButtonPriceForQuantity();
    }

    if (this.isUpdatingQuantity || this.initialQuantitySynced) return;

    // Strategy 1: Check the offers block directly for selected offer quantity
    // This is the most reliable since it reads the actual selected offer state
    const selectedOffer = document.querySelector('.offer.is-selected[data-quantity]');
    if (selectedOffer) {
      const offerQty = parseInt(selectedOffer.dataset.computedRequestedQuantity) ||
        parseInt(selectedOffer.dataset.quantity) || 1;
      if (offerQty !== this.quantity) {
        this.updateQuantityDisplay(offerQty);
        this.initialQuantitySynced = true;
        return;
      } else if (offerQty > 1) {
        this.initialQuantitySynced = true;
        return;
      }
    }

    // Strategy 2: read the page's quantity field, which the getter finds fresh each time.
    const mainField = this.mainQuantityField;
    if (mainField) {
      const currentQty = parseInt(mainField.value) || 1;
      if (currentQty !== this.quantity) {
        this.updateQuantityDisplay(currentQty);
        this.initialQuantitySynced = true;
      } else if (currentQty > 1) {
        // If quantity is already > 1 and matches, we've successfully synced
        this.initialQuantitySynced = true;
      }
    }
  }

  removeExternalQuantityListeners() {
    document.removeEventListener('offers:quantity-changed', this.handleExternalQuantityChange);
    if (this.onOffersReady) document.removeEventListener('offers:ready', this.onOffersReady);
    document.removeEventListener('subscription:type-changed', this.handleSubscriptionChange);
    document.removeEventListener('offers:prices-updated', this.handleOfferPricesUpdated);

    document.removeEventListener('change', this.handleMainFormQuantityChange);
  }

  handleExternalQuantityChange(event) {
    if (this.isUpdatingQuantity) return;

    const detail = event.detail || {};
    const requestedQuantity = parseInt(detail.requestedQuantity, 10);
    const quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0
      ? requestedQuantity
      : detail.quantity;
    if (quantity && quantity !== this.quantity) {
      this.updateQuantityDisplay(quantity);
    }

    // Mark initial sync as complete since we received the offers event
    this.initialQuantitySynced = true;
  }

  handleMainFormQuantityChange(event) {
    if (this.isUpdatingQuantity) return;

    /* Delegated from the document, so reject everything else before running a lookup. */
    const target = event && event.target;
    if (target && target.name !== 'quantity') return;

    const field = this.mainQuantityField;
    if (!field || (target && target !== field)) return;

    const qty = parseInt(field.value) || 1;
    if (qty !== this.quantity) {
      this.updateQuantityDisplay(qty);
    }
  }

  handleSubscriptionChange(event) {
    const { type, sellingPlan, compareBasis } = event.detail || {};
    this.currentSubscriptionType = type || 'onetime';
    this.currentSellingPlan = sellingPlan;
    this.currentCompareBasis = compareBasis || 'onetime';
    this.subscriptionStateKnown = true;

    // Update prices based on subscription type
    this.updateButtonPriceForSubscription();
  }

  handleOfferPricesUpdated(event) {
    const detail = event?.detail || {};
    const selectedEventOffer = this.getSelectedOfferFromEvent(detail);

    if (selectedEventOffer && !this.offerMatchesConfiguredProduct(selectedEventOffer)) {
      return;
    }

    if (detail.sectionId) {
      this.lastOfferEventSectionId = String(detail.sectionId);
    }
    if (detail.blockId) {
      this.lastOfferEventBlockId = String(detail.blockId);
    }

    const requestedQuantity = parseInt(detail.requestedQuantity, 10);
    const quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0
      ? requestedQuantity
      : parseInt(detail.quantity, 10);
    if (Number.isFinite(quantity) && quantity > 0) {
      this.quantity = quantity;
      if (this.quantityInput && parseInt(this.quantityInput.value, 10) !== quantity) {
        this.quantityInput.value = quantity;
      }
      if (this.quantitySelect && parseInt(this.quantitySelect.value, 10) !== quantity) {
        this.quantitySelect.value = quantity;
      }
    }

    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();
  }

  offerMatchesConfiguredProduct(offer) {
    if (!offer) return false;
    if (!this.config.productHandle) return true;

    const offerHandle = String(offer.dataset.productHandle || '').trim();
    return !offerHandle || offerHandle === this.config.productHandle;
  }

  getSelectedOfferFromEvent(detail) {
    const sectionId = String(detail?.sectionId || '').trim();
    const blockId = String(detail?.blockId || '').trim();

    let selector = '.offer.is-selected';
    if (blockId) {
      selector += `[data-block-id="${blockId.replace(/"/g, '\\"')}"]`;
    }

    if (sectionId) {
      return document.querySelector(`#shopify-section-${sectionId} ${selector}`);
    }

    if (blockId) {
      return document.querySelector(selector);
    }

    return null;
  }

  getSelectedOfferElement() {
    const candidates = [];

    if (this.lastOfferEventSectionId) {
      candidates.push(
        document.querySelector(`#shopify-section-${this.lastOfferEventSectionId} .offer.is-selected`)
      );
    }

    if (this.lastOfferEventBlockId) {
      candidates.push(
        document.querySelector(
          `.offer.is-selected[data-block-id="${this.lastOfferEventBlockId.replace(/"/g, '\\"')}"]`
        )
      );
    }

    if (this.sectionId) {
      candidates.push(document.querySelector(`#shopify-section-${this.sectionId} .offer.is-selected`));
    }

    for (const candidate of candidates) {
      if (this.offerMatchesConfiguredProduct(candidate)) {
        return candidate;
      }
    }

    const selectedOffers = Array.from(document.querySelectorAll('.offer.is-selected'));
    if (selectedOffers.length === 0) return null;

    const matchingOffer = selectedOffers.find((offer) => this.offerMatchesConfiguredProduct(offer));
    if (matchingOffer) return matchingOffer;

    return this.config.productHandle ? null : selectedOffers[0];
  }

  getSelectedOfferComputedTotals() {
    const selectedOffer = this.getSelectedOfferElement();
    if (!selectedOffer) return null;

    const tierTotalPrice = parseInt(selectedOffer.dataset.computedTotalPrice, 10);
    const tierComparePrice = parseInt(selectedOffer.dataset.computedComparePrice, 10);
    const tierQuantity = Math.max(parseInt(selectedOffer.dataset.quantity, 10) || this.quantity || 1, 1);

    // The offers block publishes totals for the shopper's full quantity, which
    // includes any units picked on top of the tier.
    const requestedQuantity = parseInt(selectedOffer.dataset.computedRequestedQuantity, 10);
    const requestedTotalPrice = parseInt(selectedOffer.dataset.computedRequestedTotalPrice, 10);
    const requestedComparePrice = parseInt(selectedOffer.dataset.computedRequestedComparePrice, 10);

    const quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0 ? requestedQuantity : tierQuantity;
    const totalPrice = Number.isFinite(requestedTotalPrice) ? requestedTotalPrice : tierTotalPrice;
    const comparePrice = Number.isFinite(requestedComparePrice) ? requestedComparePrice : tierComparePrice;

    if (!Number.isFinite(totalPrice)) return null;

    return {
      quantity,
      totalPrice: Math.max(0, totalPrice),
      comparePrice: Number.isFinite(comparePrice) ? Math.max(0, comparePrice) : Math.max(0, totalPrice)
    };
  }

  getOffersComponentState() {
    const selectedOffer = this.getSelectedOfferElement();
    const offersRoot = selectedOffer?.closest('.offers');
    const state = offersRoot?.sugarOffers || null;
    return typeof state?.setRequestedQuantity === 'function' ? state : null;
  }

  // Hands a free-form quantity to the offers block, which picks the tier that
  // quantity qualifies for and keeps the leftover units as extras. Returns the
  // quantity actually applied (raised when it fell below the smallest tier), or
  // null when there is no offers block to sync with.
  requestOfferQuantity(qty) {
    const state = this.getOffersComponentState();
    if (!state) return null;

    const applied = Math.max(parseInt(state.setRequestedQuantity(qty), 10) || qty, 1);
    this.quantity = applied;

    if (this.quantityInput && parseInt(this.quantityInput.value, 10) !== applied) {
      this.quantityInput.value = applied;
    }
    if (this.quantitySelect && parseInt(this.quantitySelect.value, 10) !== applied) {
      this.quantitySelect.value = applied;
    }

    return applied;
  }

  getProductForm() {
    const selectedOffer = this.getSelectedOfferElement();
    const scope = selectedOffer?.closest('.featured-product-info') ||
      selectedOffer?.closest('.shopify-section') ||
      this.mainProductHost() ||
      document;

    const productFormEl = scope.querySelector?.('product-form');
    if (productFormEl) {
      const productForm = productFormEl.querySelector('form[data-type="add-to-cart-form"], form');
      if (productForm) return productForm;
    }

    return scope.querySelector?.('form[action*="/cart/add"]') ||
      document.querySelector('product-form form, form[action*="/cart/add"]');
  }

  getSellingPlanId(form) {
    const sellingPlanInput = form?.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput && !sellingPlanInput.disabled && sellingPlanInput.value) {
      const sellingPlanId = parseInt(sellingPlanInput.value, 10);
      if (Number.isFinite(sellingPlanId)) return sellingPlanId;
    }

    if (this.currentSubscriptionType === 'subscribe' && this.currentSellingPlan?.id) {
      const sellingPlanId = parseInt(this.currentSellingPlan.id, 10);
      if (Number.isFinite(sellingPlanId)) return sellingPlanId;
    }

    return null;
  }

  getOfferSelectedVariantId(offer, slot, componentState) {
    const blockId = String(offer?.dataset?.blockId || '').trim();
    const stateVariantId = String(componentState?.selectedVariants?.[blockId]?.[slot] || '').trim();
    if (stateVariantId) return stateVariantId;

    const container = offer?.querySelector(`.offer__variants-slot[data-slot="${slot}"]`);
    const dropdownVariantId = String(container?.querySelector('.offer__variant-select')?.value || '').trim();
    if (dropdownVariantId) return dropdownVariantId;

    const pillVariantId = String(container?.querySelector('.offer__variant-pill.is-active')?.dataset.variantId || '').trim();
    if (pillVariantId) return pillVariantId;

    return '';
  }

  getRemovedOfferGifts() {
    try {
      const storageKey = `sugar_offers_removed_gifts_${window.location.pathname}`;
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  }

  buildOfferCartItems(offer, form) {
    if (!offer) return null;

    const quantity = Math.max(parseInt(offer.dataset.quantity, 10) || 1, 1);
    // Units the shopper stacked on top of the tier via the quantity selector.
    const requestedQuantity = Math.max(
      parseInt(offer.dataset.computedRequestedQuantity, 10) || quantity,
      quantity
    );
    const extraQuantity = requestedQuantity - quantity;
    const sellingPlanId = this.getSellingPlanId(form);
    const items = [];
    const blockId = String(offer.dataset.blockId || '').trim();
    const offerVariantId = String(offer.dataset.variantId || '').trim();
    const formVariantId = String(form?.querySelector('[name="id"]')?.value || '').trim();
    const currentVariantId = String(this.currentVariantId || '').trim();
    const offerVariantIds = String(offer.dataset.offerVariantIds || '')
      .split(',')
      .map((id) => String(id || '').trim())
      .filter(Boolean);
    const offerVariantIdSet = new Set(offerVariantIds);
    const hasOfferVariantSet = offerVariantIdSet.size > 0;
    const isOfferVariantId = (id) => {
      if (!id) return false;
      return !hasOfferVariantSet || offerVariantIdSet.has(String(id));
    };
    const firstOfferVariantId = offerVariantIds[0] || '';
    const isLocked = offer.dataset.variantLocked === 'true';
    const componentState = offer.closest('.offers')?.sugarOffers || null;

    let fallbackVariantId = '';
    if (isOfferVariantId(offerVariantId)) fallbackVariantId = offerVariantId;
    if (!fallbackVariantId && isOfferVariantId(formVariantId)) fallbackVariantId = formVariantId;
    if (!fallbackVariantId && isOfferVariantId(currentVariantId)) fallbackVariantId = currentVariantId;
    if (!fallbackVariantId) fallbackVariantId = firstOfferVariantId || offerVariantId || formVariantId || currentVariantId;

    for (let slot = 1; slot <= quantity; slot++) {
      const slotVariantId = isLocked
        ? offerVariantId
        : this.getOfferSelectedVariantId(offer, slot, componentState);
      const resolvedVariantId = isOfferVariantId(slotVariantId) ? slotVariantId : fallbackVariantId;
      const parsedVariantId = parseInt(resolvedVariantId, 10);
      if (!Number.isFinite(parsedVariantId)) continue;

      const item = { id: parsedVariantId, quantity: 1 };
      if (sellingPlanId) item.selling_plan = sellingPlanId;
      items.push(item);
    }

    if (items.length === 0) {
      const parsedFallbackId = parseInt(fallbackVariantId, 10);
      if (Number.isFinite(parsedFallbackId)) {
        const fallbackItem = { id: parsedFallbackId, quantity: requestedQuantity };
        if (sellingPlanId) fallbackItem.selling_plan = sellingPlanId;
        items.push(fallbackItem);
      }
    } else if (extraQuantity > 0) {
      const extraSourceId = isLocked
        ? offerVariantId
        : (this.getOfferSelectedVariantId(offer, 1, componentState) || fallbackVariantId);
      const parsedExtraId = parseInt(isOfferVariantId(extraSourceId) ? extraSourceId : fallbackVariantId, 10);
      if (Number.isFinite(parsedExtraId)) {
        const extraItem = { id: parsedExtraId, quantity: extraQuantity };
        if (sellingPlanId) extraItem.selling_plan = sellingPlanId;
        items.push(extraItem);
      }
    }

    // The tier's own lines carry what an app put on the product form as properties[...].
    const formProperties = this.formLineProperties(form);
    if (Object.keys(formProperties).length) {
      items.forEach((item) => {
        item.properties = Object.assign({}, formProperties);
      });
    }

    const removedGifts = this.getRemovedOfferGifts();
    const qualifyingProductId = offer.dataset.productId || '';
    offer.querySelectorAll('.sugar-offer-gift[data-variant-id], .offer__perk-gift[data-variant-id]').forEach((giftEl) => {
      const giftType = giftEl.dataset.giftType;
      const giftVariantIdRaw = String(giftEl.dataset.variantId || '').trim();
      const giftVariantId = parseInt(giftVariantIdRaw, 10);
      if (!Number.isFinite(giftVariantId)) return;
      if (blockId && removedGifts[`${blockId}_${giftVariantIdRaw}`] === true) return;

      if (giftType === 'upsell') {
        const checkbox = giftEl.classList.contains('is-checked') ? giftEl : giftEl.querySelector('.offer__gift-checkbox');
        if (!checkbox?.classList.contains('is-checked')) return;
      }

      items.push({
        id: giftVariantId,
        quantity: 1,
        properties: {
          _offer_gift: blockId,
          _offer_gift_type: giftType || 'gift',
          _gift_for_product: qualifyingProductId
        }
      });
    });

    this.quantity = requestedQuantity;
    return items;
  }

  /*
   * A tier builds one line per unit. Identical lines (same variant, plan, properties and parent)
   * go to the cart as one line of their summed quantity, as the Offers block sends them: Shopify
   * merges them anyway, and every separate line in the add request costs it server time.
   */
  static mergeIdenticalLines(items) {
    const merged = [];
    const byKey = new Map();
    items.forEach((item) => {
      const key = JSON.stringify([item.id, item.selling_plan || null, item.properties || {}, item.parent_id || null]);
      const line = byKey.get(key);
      if (line) {
        line.quantity += item.quantity;
        return;
      }
      const copy = Object.assign({}, item);
      byKey.set(key, copy);
      merged.push(copy);
    });
    return merged;
  }

  buildCartItems(selectedOffer = this.getSelectedOfferElement()) {
    const form = this.getProductForm();
    const offerItems = this.buildOfferCartItems(selectedOffer, form);
    if (offerItems?.length) return offerItems;

    const formQuantity = parseInt(form?.querySelector('[name="quantity"]')?.value, 10);
    const stickyInputQuantity = parseInt(this.quantityInput?.value || this.quantitySelect?.value, 10);
    const quantity = Math.max(
      Number.isFinite(formQuantity) ? formQuantity : (Number.isFinite(stickyInputQuantity) ? stickyInputQuantity : this.quantity),
      1
    );
    const variantId = parseInt(this.currentVariantId || form?.querySelector('[name="id"]')?.value, 10);
    if (!Number.isFinite(variantId)) return [];

    this.quantity = quantity;
    const item = { id: variantId, quantity };
    const sellingPlanId = this.getSellingPlanId(form);
    if (sellingPlanId) item.selling_plan = sellingPlanId;
    const formProperties = this.formLineProperties(form);
    if (Object.keys(formProperties).length) item.properties = formProperties;
    return [item];
  }

  // The product form's properties[...] fields (an app's personalisation, say), minus the offer
  // stamp and empty answers, so the bar's add carries them the way the form's own submit does.
  formLineProperties(form) {
    const properties = {};
    if (!form) return properties;
    try {
      new FormData(form).forEach((value, key) => {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (!match || match[1] === '_sugar_offer' || typeof value !== 'string' || value === '') return;
        properties[match[1]] = value;
      });
    } catch (e) {
      /* not a form */
    }
    return properties;
  }

  updateButtonPriceForSubscription() {
    const offerTotals = this.getSelectedOfferComputedTotals();
    if (offerTotals) {
      this.quantity = offerTotals.quantity;
      this.updateButtonPriceForQuantity();
      return;
    }

    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();
  }

  /*
   * The unit price and unit compare price under the page's subscription plan, or null with no
   * plan on. The compare price follows the subscription block's Compare price shows: the
   * compare-at when asked for and set, else the one-time price (with no offer on the page that
   * is also the price before discounts).
   */
  subscribedUnit(base, compareAt) {
    if (this.currentSubscriptionType !== 'subscribe' || !this.currentSellingPlan) return null;
    const adjustment = this.currentSellingPlan.price_adjustments?.[0];
    let price = base;
    if (adjustment?.value_type === 'percentage') {
      price = Math.round(base * (100 - adjustment.value) / 100);
    } else if (adjustment?.value_type === 'fixed_amount') {
      price = Math.max(0, base - adjustment.value);
    } else if (adjustment?.value_type === 'price') {
      price = Math.max(0, adjustment.value);
    }
    const basis = this.currentCompareBasis || 'onetime';
    const compare = (basis === 'maximum' || basis === 'compare_at') && compareAt > 0 ? compareAt : base;
    return { price, compare };
  }

  updateQuantityDisplay(qty) {
    this.quantity = qty;

    // Update sticky bar's quantity selector UI
    if (this.quantityInput && parseInt(this.quantityInput.value) !== qty) {
      this.quantityInput.value = qty;
    }
    if (this.quantitySelect && parseInt(this.quantitySelect.value) !== qty) {
      this.quantitySelect.value = qty;
    }

    // Update prices
    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();
  }

  syncToMainForm(qty) {
    const field = this.mainQuantityField;
    if (!field || this.isUpdatingQuantity) return;

    this.isUpdatingQuantity = true;

    if (parseInt(field.value) !== qty) {
      field.value = qty;
      field.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Reset flag after a short delay to allow event propagation
    setTimeout(() => {
      this.isUpdatingQuantity = false;
    }, 50);
  }

  subscribeToEvents() {
    // Subscribe to variant changes from main product
    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleMainVariantChange
      );

      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        this.handleCartUpdate
      );
    }
  }

  unsubscribeFromEvents() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  initializeVisibility() {
    // Docked mode: use scroll-based sticking
    if (this.config.docked) {
      this.isVisible = true;
      this.setupDockedMode();
      return;
    }

    if (this.config.visibility === 'always') {
      // Small delay to allow CSS transition
      requestAnimationFrame(() => {
        this.show();
      });
    } else {
      // Set up observer for buy button
      this.setupBuyButtonObserver();
    }
  }

  setupDockedMode() {
    this.isStuck = false;
    this.isDocked = false;
    this.dockedScrollTicking = false;

    // Create placeholder to prevent content jump
    this.dockedPlaceholder = document.createElement('div');
    this.dockedPlaceholder.style.margin = '0';
    this.dockedPlaceholder.style.padding = '0';
    this.dockedPlaceholder.style.border = 'none';
    this.dockedPlaceholder.style.visibility = 'hidden';
    this.parentNode.insertBefore(this.dockedPlaceholder, this.nextSibling);

    if (this.config.position === 'top') {
      // Top mode: starts in document flow, sticks when scrolled past
      requestAnimationFrame(() => {
        const rect = this.getBoundingClientRect();
        this.dockedHeight = Math.round(rect.height);
        this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
        this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
        this.dockedPlaceholder.style.display = 'none';
        this.startDockedListeners();
      });
    } else {
      // Cache element height for bottom mode
      this.dockedHeight = this.offsetHeight;
      // Bottom mode: starts fixed, docks when scrolled to
      this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
      this.dockedPlaceholder.style.display = 'block';
      this.startDockedListeners();
      this.markStickyShown();
    }
  }

  startDockedListeners() {
    // Bind handlers
    this.onDockedScroll = this.handleDockedScroll.bind(this);
    this.onDockedResize = this.handleDockedResize.bind(this);

    window.addEventListener('scroll', this.onDockedScroll, { passive: true });
    window.addEventListener('resize', this.onDockedResize, { passive: true });

    // Initial check
    this.handleDockedScroll();
  }

  handleDockedResize() {
    // Recalculate position and height on resize
    const rect = this.getBoundingClientRect();
    if (this.config.position === 'top' && !this.isStuck) {
      this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
      this.dockedHeight = Math.round(rect.height);
    } else {
      this.dockedHeight = this.offsetHeight;
    }
    this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
  }

  handleDockedScroll() {
    if (!this.config.docked) return;

    // Skip if element is hidden (e.g., wrong viewport in theme editor)
    // This is needed because desktop/mobile are separate elements with separate placeholders
    if (this.offsetHeight === 0) {
      // Element is hidden - make sure placeholder is also hidden
      if (this.isStuck) {
        this.isStuck = false;
        this.classList.remove('is-stuck');
        this.dockedPlaceholder.style.display = 'none';
      }
      return;
    }

    // Throttle with requestAnimationFrame to prevent excessive calculations
    if (!this.dockedScrollTicking) {
      this.dockedScrollTicking = true;
      requestAnimationFrame(() => {
        if (this.config.position === 'top') {
          this.handleTopDockedScroll();
        } else {
          this.handleBottomDockedScroll();
        }
        this.dockedScrollTicking = false;
      });
    }
  }

  handleTopDockedScroll() {
    // Round scroll position to avoid sub-pixel jitter
    const scrollY = Math.round(window.scrollY);

    // Get the current header offset - this is where the sticky section will be positioned
    // When header is hidden: offset = 0, when header is visible/sticky: offset = header height
    const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-offset')) || 0;

    // Calculate the stick threshold accounting for header offset
    // The section should stick when its viewport position reaches the header offset
    // Section viewport top = dockedOriginalTop - scrollY
    // Stick when: dockedOriginalTop - scrollY <= headerOffset
    // Which means: scrollY >= dockedOriginalTop - headerOffset
    const stickThreshold = this.dockedOriginalTop - headerOffset;

    // Hysteresis buffer: require scrolling a bit past threshold to unstick
    // This prevents rapid toggling when at the boundary
    const unstickBuffer = 5;

    if (!this.isStuck) {
      // Check if we should stick
      if (scrollY >= stickThreshold) {
        this.isStuck = true;
        this.dockedPlaceholder.style.display = 'block';
        this.classList.add('is-stuck');
        this.markStickyShown();
      }
    } else {
      // Already stuck - check if we should unstick
      // Recalculate threshold in case header offset changed while stuck
      const currentStickThreshold = this.dockedOriginalTop - headerOffset;
      if (scrollY < currentStickThreshold - unstickBuffer) {
        this.isStuck = false;
        this.dockedPlaceholder.style.display = 'none';
        this.classList.remove('is-stuck');
      }
    }
  }

  handleBottomDockedScroll() {
    const viewportHeight = window.innerHeight;
    const buffer = 5;

    if (!this.isDocked) {
      // Bar is fixed at bottom of viewport
      // Its visual top position is: viewportHeight - barHeight
      const barVisualTop = viewportHeight - this.dockedHeight;

      // Get placeholder position (where bar should dock)
      const placeholderRect = this.dockedPlaceholder.getBoundingClientRect();

      // Dock when placeholder has scrolled up to meet the bar's current position
      // This creates a seamless visual transition
      if (placeholderRect.top <= barVisualTop) {
        this.dockScrollThreshold = window.scrollY;
        this.isDocked = true;
        this.dockedPlaceholder.style.display = 'none';
        this.classList.add('is-docked');
      }
    } else {
      // Bar is docked - undock when scrolled back above threshold
      if (window.scrollY < this.dockScrollThreshold - buffer) {
        this.isDocked = false;
        this.dockedPlaceholder.style.display = 'block';
        this.classList.remove('is-docked');
      }
    }
  }

  // A replaced buy button (theme editor re-render, a section re-render on variant change) reads as
  // scrolled past, which left the bar up everywhere: watch the new one instead.
  rewatchBuyButton() {
    if (this.config.docked || this.config.visibility === 'always') return;
    if (this.watchedBuyButton && this.watchedBuyButton.isConnected) return;
    this.setupBuyButtonObserver();
  }

  setupBuyButtonObserver() {
    if (this.buyButtonObserver) {
      this.buyButtonObserver.disconnect();
      this.buyButtonObserver = null;
    }

    // Find the first buy button on the page
    const buyButton = document.querySelector(
      'product-form .product-form__submit, ' +
      '.product-buy-button-block button[type="submit"], ' +
      '[data-sticky-trigger], ' +
      'form[action*="/cart/add"] button[type="submit"]'
    );

    if (!buyButton) {
      // No buy button found, show sticky bar
      this.buyButtonVisible = false; // Treat as if buy button is not visible
      requestAnimationFrame(() => {
        this.show();
      });
      return;
    }

    // Create intersection observer
    this.watchedBuyButton = buyButton;
    this.buyButtonObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];

        if (!entry.target.isConnected) {
          this.setupBuyButtonObserver();
          return;
        }

        if (entry.isIntersecting) {
          // Buy button visible in viewport - hide sticky bar
          this.buyButtonVisible = true;
          this.hide();
        } else {
          // Buy button not visible - check if it's above or below viewport
          const rect = entry.boundingClientRect;
          const isBelowViewport = rect.top >= window.innerHeight;

          if (isBelowViewport) {
            // Haven't scrolled to buy button yet - keep sticky hidden
            this.buyButtonVisible = true;
            this.hide();
          } else {
            // Scrolled past buy button - show sticky
            this.buyButtonVisible = false;
            if (!this.shouldHideAtBottom()) {
              this.show();
            }
          }
        }
      },
      {
        threshold: 0,
        rootMargin: '-50px 0px 0px 0px'
      }
    );

    this.buyButtonObserver.observe(buyButton);
  }

  // === EVENT HANDLERS ===

  onScroll() {
    if (!this.config.hideAtBottom) return;

    if (this.shouldHideAtBottom()) {
      this.hide();
    } else if (this.shouldBeVisible()) {
      this.show();
    }
  }

  onVariantChange(event) {
    const newVariantId = event.target.value;
    const selectedOption = event.target.selectedOptions[0];

    this.currentVariantId = newVariantId;

    // Update price display
    if (selectedOption) {
      const price = selectedOption.dataset.price;
      const comparePrice = selectedOption.dataset.comparePrice;
      this.updatePriceDisplay(price, comparePrice);

      // Update image
      const imageUrl = selectedOption.dataset.image;
      if (imageUrl && this.variantImage) {
        this.variantImage.src = imageUrl;
      }
    }

    // Sync with main product
    this.syncMainProductVariant(newVariantId);

    // Update button state based on availability
    const isDisabled = selectedOption && selectedOption.disabled;
    if (this.button) {
      this.button.disabled = isDisabled;
    }
  }

  onQuantityChange(event) {
    const newQty = Math.max(1, parseInt(event.target.value) || 1);
    this.quantity = newQty;

    const appliedQty = this.requestOfferQuantity(newQty);
    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();

    // Sync to main form so cart addition uses correct quantity
    // (the offers block owns the form quantity when it is present)
    if (appliedQty === null) this.syncToMainForm(newQty);
  }

  adjustQuantity(delta) {
    const newQuantity = Math.max(1, this.quantity + delta);
    this.quantity = newQuantity;
    if (this.quantityInput) {
      this.quantityInput.value = newQuantity;
    }

    const appliedQty = this.requestOfferQuantity(newQuantity);
    this.updateInfoPriceForQuantity();
    this.updateButtonPriceForQuantity();

    // Sync to main form so cart addition uses correct quantity
    if (appliedQty === null) this.syncToMainForm(newQuantity);
  }

  updateInfoPriceForQuantity() {
    const offerTotals = this.getSelectedOfferComputedTotals();
    if (offerTotals) {
      this.quantity = offerTotals.quantity;

      if (this.infoCurrentPriceElement) {
        this.infoCurrentPriceElement.textContent = this.formatMoney(offerTotals.totalPrice);
      }

      if (this.infoComparePriceElement) {
        if (offerTotals.comparePrice > offerTotals.totalPrice) {
          this.infoComparePriceElement.textContent = this.formatMoney(offerTotals.comparePrice);
          this.infoComparePriceElement.style.display = '';
        } else {
          this.infoComparePriceElement.style.display = 'none';
        }
      }
      return;
    }

    if (!this.infoPriceContainer || !this.basePrice) return;

    const sub = this.subscribedUnit(this.basePrice, this.baseComparePrice);
    const unitPrice = sub ? sub.price : this.basePrice;
    const unitCompare = sub ? sub.compare : this.baseComparePrice;
    const totalPrice = unitPrice * this.quantity;
    const totalComparePrice = unitCompare * this.quantity;

    if (this.infoCurrentPriceElement) {
      this.infoCurrentPriceElement.textContent = this.formatMoney(totalPrice);
    }

    if (this.infoComparePriceElement) {
      if (unitCompare > unitPrice) {
        this.infoComparePriceElement.textContent = this.formatMoney(totalComparePrice);
        this.infoComparePriceElement.style.display = '';
      } else {
        this.infoComparePriceElement.style.display = 'none';
      }
    }
  }

  updateButtonPriceForQuantity() {
    const offerTotals = this.getSelectedOfferComputedTotals();
    if (offerTotals) {
      this.quantity = offerTotals.quantity;

      this.currentPriceElements.forEach((currentPriceElement) => {
        currentPriceElement.textContent = this.formatMoney(offerTotals.totalPrice);
      });

      this.comparePriceElements.forEach((comparePriceElement) => {
        if (offerTotals.comparePrice > offerTotals.totalPrice) {
          comparePriceElement.textContent = this.formatMoney(offerTotals.comparePrice);
          comparePriceElement.style.display = '';
        } else {
          comparePriceElement.style.display = 'none';
        }
      });
      return;
    }

    if (this.priceElements.length === 0 || this.buttonBasePrice === undefined || this.buttonBasePrice === null) return;

    const sub = this.subscribedUnit(this.buttonBasePrice, this.buttonBaseComparePrice);
    const unitPrice = sub ? sub.price : this.buttonBasePrice;
    const unitCompare = sub ? sub.compare : this.buttonBaseComparePrice;
    const totalPrice = unitPrice * this.quantity;
    const totalComparePrice = unitCompare * this.quantity;

    this.currentPriceElements.forEach((currentPriceElement) => {
      currentPriceElement.textContent = this.formatMoney(totalPrice);
    });

    this.comparePriceElements.forEach((comparePriceElement) => {
      if (unitCompare && unitCompare > unitPrice) {
        comparePriceElement.textContent = this.formatMoney(totalComparePrice);
        comparePriceElement.style.display = '';
      } else {
        comparePriceElement.style.display = 'none';
      }
    });
  }

  onButtonClick(event) {
    event.preventDefault();

    if (this.isLoading || this.button.disabled) return;

    if (this.config.buttonAction === 'scroll') {
      this.scrollToAnchor();
    } else {
      this.trackSticky('click', 'atc');
      this.addToCart();
    }
  }

  /*
   * Whether a variant change is the page's own product moving: a variant of the bar's product,
   * announced by the main product section. A Featured Product picker elsewhere on the page, or
   * an offer tier selling another product, leaves the bar where it is.
   */
  isOwnVariantChange(data) {
    const ids = String(this.dataset.variantIds || '').split(',').filter(Boolean);
    if (ids.length && ids.indexOf(String(data.variant.id)) === -1) return false;
    const sectionId = data.sectionId ? String(data.sectionId) : '';
    if (!sectionId) return true;
    const host = this.mainProductHost();
    return !host || String(host.dataset.section || '') === sectionId;
  }

  /* The main product section's root for the bar's product (Product Main ahead of a Featured Product). */
  mainProductHost() {
    let host = null;
    document.querySelectorAll('product-info[data-main-product][data-product-id="' + (this.dataset.productId || '') + '"]').forEach((el) => {
      if (!host || (el.classList.contains('sugar-product-main') && !host.classList.contains('sugar-product-main'))) host = el;
    });
    return host;
  }

  handleMainVariantChange(event) {
    this.rewatchBuyButton();
    const { data } = event;
    if (!data || !data.variant || !this.isOwnVariantChange(data)) return;

    const variant = data.variant;
    this.currentVariantId = variant.id;

    // Update variant selector
    if (this.variantSelect && this.variantSelect.value !== String(variant.id)) {
      this.variantSelect.value = variant.id;
    }

    // Update base prices for info price
    this.basePrice = variant.price;
    this.baseComparePrice = variant.compare_at_price || 0;

    // Update info price container data attributes
    if (this.infoPriceContainer) {
      this.infoPriceContainer.dataset.basePrice = variant.price;
      this.infoPriceContainer.dataset.baseComparePrice = variant.compare_at_price || 0;
    }

    // Update base prices for button price
    this.buttonBasePrice = variant.price;
    this.buttonBaseComparePrice = variant.compare_at_price || 0;

    // Update button price container data attributes
    this.priceElements.forEach((priceElement) => {
      priceElement.dataset.basePrice = variant.price;
      priceElement.dataset.baseComparePrice = variant.compare_at_price || 0;
    });

    // Update info price display with quantity
    this.updateInfoPriceForQuantity();

    // Update button price display with quantity
    this.updateButtonPriceForQuantity();

    // Update image. Never let an unexpected shape here abort the button state
    // handling below.
    const variantImageSrc = variant.featured_image?.src;
    if (typeof variantImageSrc === 'string' && this.variantImage) {
      this.variantImage.src = variantImageSrc.replace(/(\.[^.]+)$/, '_100x$1');
    }

    // Update button state
    if (this.button) {
      this.button.disabled = !variant.available;

      // Update button text
      if (this.buttonText) {
        if (!variant.available) {
          this.buttonText.innerHTML = window.variantStrings?.soldOutHtml || window.variantStrings?.soldOut || 'Sold Out';
          this.button.classList.add('is-sold-out');
          this.cacheButtonPriceElements();
        } else {
          this.button.classList.remove('is-sold-out');
          const originalText = this.buttonTextOriginalHtml || this.buttonText.dataset.originalText;
          if (originalText) {
            this.buttonText.innerHTML = originalText;
          }
          this.cacheButtonPriceElements();
          this.updateButtonPriceForQuantity();
        }
      }
    }
  }

  handleCartUpdate(event) {
    if (this.pendingCheckout && this.config.skipCart) {
      this.pendingCheckout = false;
      window.location.href = '/checkout';
    }
  }

  // === DARK-SECTION LOOK ===

  // Only the Sticky Buy Button with On Dark Sections colors carries data-dark-look.
  setupBackdropWatch() {
    if (!this.hasAttribute('data-dark-look') || !this.button) return;
    this.backdropTones = new WeakMap();
    this.onDark = false;
    this.onBackdropScroll = () => {
      if (this.backdropTicking) return;
      this.backdropTicking = true;
      requestAnimationFrame(() => {
        this.backdropTicking = false;
        this.updateBackdrop();
      });
    };
    this.onBackdropResize = () => {
      this.backdropTones = new WeakMap();
      this.onBackdropScroll();
    };
    window.addEventListener('scroll', this.onBackdropScroll, { passive: true });
    window.addEventListener('resize', this.onBackdropResize, { passive: true });
    this.onBackdropScroll();
  }

  updateBackdrop() {
    if (!this.backdropTones || !this.offsetHeight) return;
    const r = this.button.getBoundingClientRect();
    if (!r.width) return;
    // While the bar slides in it is still off screen: sample where it comes to rest.
    const y = Math.min(Math.max(r.top + r.height / 2, r.height / 2), window.innerHeight - r.height / 2);
    const dark = this.backdropIsDark(r.left + r.width / 2, y, r.width * 0.8);
    if (dark === this.onDark) return;
    this.onDark = dark;
    this.toggleAttribute('data-on-dark', dark);
  }

  // The first background behind the button that spans most of its width decides, so a card or a
  // chip narrower than that passes behind without switching the look. Other buttons and pictures
  // are judged by what they sit on.
  backdropIsDark(x, y, minWidth) {
    for (const el of document.elementsFromPoint(x, y)) {
      if (this.contains(el)) continue;
      let tone = this.backdropTones.get(el);
      if (tone === undefined) {
        tone = this.backdropTone(el, minWidth);
        this.backdropTones.set(el, tone);
      }
      if (tone !== 'clear') return tone === 'dark';
    }
    return false;
  }

  backdropTone(el, minWidth) {
    if (/^(A|BUTTON|IMG|VIDEO|PICTURE|CANVAS|IFRAME|svg)$/.test(el.tagName)) return 'clear';
    if (el.getBoundingClientRect().width < minWidth) return 'clear';
    const cs = getComputedStyle(el);
    let rgba = null;
    if (!cs.backgroundImage.includes('url(')) {
      const stops = [...cs.backgroundImage.matchAll(/rgba?\(([^)]+)\)/g)].map((m) => this.parseRgb(m[1]));
      if (stops.length && stops.reduce((a, c) => a + c[3], 0) / stops.length >= 0.5) {
        rgba = [0, 1, 2].map((i) => stops.reduce((a, c) => a + c[i], 0) / stops.length);
      }
    }
    if (!rgba) {
      const c = this.parseRgb((cs.backgroundColor.match(/rgba?\(([^)]+)\)/) || [])[1]);
      if (c && c[3] >= 0.5) rgba = c;
    }
    if (!rgba) return 'clear';
    const lin = rgba.slice(0, 3).map((v) => {
      const s = v / 255;
      return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2] < 0.18 ? 'dark' : 'light';
  }

  parseRgb(body) {
    if (!body) return null;
    const p = body.split(/[\s,/]+/).filter(Boolean).map(parseFloat);
    return [p[0], p[1], p[2], p.length > 3 ? p[3] : 1];
  }

  // === VISIBILITY METHODS ===

  show() {
    if (this.isVisible) return;
    this.isVisible = true;
    this.updateBackdrop();
    this.classList.add('is-visible');
    this.markStickyShown();
  }

  hide() {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.classList.remove('is-visible');
  }

  shouldBeVisible() {
    if (this.config.visibility === 'always') return true;

    // Check if buy button is NOT visible (meaning sticky bar should show)
    return !this.buyButtonVisible;
  }

  shouldHideAtBottom() {
    if (!this.config.hideAtBottom) return false;

    const scrollPosition = window.scrollY + window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;
    const threshold = 150;

    return (pageHeight - scrollPosition) < threshold;
  }

  // === SYNC METHODS ===

  syncMainProductVariant(variantId) {
    // Find main product's variant selector/input
    const mainVariantInput = document.querySelector(
      'product-form input[name="id"], ' +
      'form[action*="/cart/add"] input[name="id"]'
    );

    if (mainVariantInput && mainVariantInput.value !== variantId) {
      mainVariantInput.value = variantId;

      // Try to trigger variant change in main product
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects) {
        // Find the select and update it
        const select = variantSelects.querySelector('select[name="id"]');
        if (select && select.value !== variantId) {
          select.value = variantId;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    this.announceVariant(variantId);
  }

  /*
   * Tell the main product section about the bar's pick the way its own Variant Picker would, so
   * the picker, the price, the gallery and an Offer List's per-unit pickers follow it. Without
   * this an Offer List kept its own picks and the bar's Add to cart added those instead.
   */
  announceVariant(variantId) {
    const id = String(variantId || '');
    const form = this.getProductForm();
    const section = form ? form.closest('.shopify-section') : null;
    if (!id || !section) return;

    const pickerRoot = section.querySelector('.sugar-product-variant-picker');
    const picker = pickerRoot ? pickerRoot.__sugarPicker : null;
    const pickerVariant = picker && Array.isArray(picker.variants)
      ? picker.variants.find((variant) => String(variant.id) === id)
      : null;
    if (pickerVariant && typeof picker.dispatch === 'function') {
      if (typeof picker.applySelection === 'function') picker.applySelection(pickerVariant);
      if (typeof picker.refresh === 'function') picker.refresh();
      picker.dispatch(pickerVariant);
      return;
    }

    const offersRoot = section.querySelector('[data-sugar-offers]');
    const offers = offersRoot ? offersRoot.sugarOffers : null;
    if (offers && typeof offers.dispatchVariantSelection === 'function') {
      offers.dispatchVariantSelection(id, { source: 'sticky-product' });
    }
  }

  // === UPDATE METHODS ===

  updatePriceDisplay(price, comparePrice) {
    this.currentPriceElements.forEach((currentPriceElement) => {
      currentPriceElement.textContent = this.formatMoney(price);
    });

    this.comparePriceElements.forEach((comparePriceElement) => {
      if (comparePrice && parseInt(comparePrice, 10) > parseInt(price, 10)) {
        comparePriceElement.textContent = this.formatMoney(comparePrice);
        comparePriceElement.style.display = '';
      } else {
        comparePriceElement.style.display = 'none';
      }
    });
  }

  // === ACTION METHODS ===

  async addToCart() {
    if (this.isLoading) return;

    const selectedOffer = this.getSelectedOfferElement();
    if (!this.currentVariantId && !selectedOffer) return;

    const items = StickyProduct.mergeIdenticalLines(this.buildCartItems(selectedOffer));
    if (items.length === 0) return;

    // Lines other blocks queued for this buy (Product Upsells and Personalization live in the
    // main product section). A required Personalization field that is empty stops the buy.
    const extrasForm = document.querySelector('product-form form, form[action*="/cart/add"]');
    const cartExtras = extrasForm && window.SugarCart && window.SugarCart.extras;
    if (cartExtras && typeof cartExtras.validate === 'function' && !cartExtras.validate(extrasForm, items)) return;
    this.setLoading(true);
    const extras = cartExtras ? cartExtras.collect(extrasForm) : [];
    if (extras.length) items.push(...extras);
    if (cartExtras && typeof cartExtras.decorate === 'function') cartExtras.decorate(extrasForm, items);

    try {
      const cartDrawer = document.querySelector('sugar-cart-drawer');
      const sections = cartDrawer?.getSectionsToRender?.().map(s => s.id) || ['sugar-cart-drawer', 'cart-icon-bubble'];

      // Set context for gift managers so they inject free gifts into this request

      const payload = { items, sections, sections_url: window.location.pathname };
      const request = cartExtras && typeof cartExtras.request === 'function'
        ? cartExtras.request(payload)
        : { body: JSON.stringify(payload), contentType: 'application/json' };
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: request.contentType ? { 'Content-Type': request.contentType, 'Accept': 'application/json' } : { 'Accept': 'application/json' },
        body: request.body
      });

      if (!response.ok) throw new Error('Add to cart failed');
      const data = await response.json();

      if (data.status) {
        this.handleError(data.description || data.message);
        return;
      }

      if (extras.length) window.SugarCart.extras.commit(extrasForm, data);

      // Skip cart → go straight to checkout
      if (this.config.skipCart) {
        window.location.href = '/checkout';
        return;
      }

      // Publish cart update (cart drawer subscription skips 'sticky-product' source)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sticky-product',
          productVariantId: this.currentVariantId,
          cartData: data
        });
      }

      // Render cart drawer via Section Rendering API response
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      } else if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

    } catch (error) {
      console.error('Sticky Product - Add to cart error:', error);
      this.handleError('Unable to add to cart');
    } finally {
      this.setLoading(false);
    }
  }

  scrollToAnchor() {
    if (!this.config.scrollAnchorId) return;

    const target = document.getElementById(this.config.scrollAnchorId);
    if (target) {
      // Account for sticky header and scroll offset
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0');
      const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerHeight - this.config.scrollOffset;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  }

  // === UTILITY METHODS ===

  setLoading(loading) {
    this.isLoading = loading;

    if (this.button) {
      this.button.disabled = loading;
      this.button.classList.toggle('loading', loading);
    }
  }

  handleError(message) {
    console.error('Sticky Product Error:', message);
    // Could show a toast notification here
  }

  trackSticky(type, detail) {
    const entity = this.sectionId || this.dataset.sectionId;
    if (!entity) return;
    const body = document.body;
    const ctx = {
      template: (body && body.getAttribute('data-sugar-template')) || '',
      path: window.location.pathname,
      product: this.config.productHandle || '',
      label: '',
      section: entity
    };
    const args = [type, 'sticky', entity, detail || '', ctx];
    if (window.SugarAnalytics) window.SugarAnalytics.track.apply(null, args);
    else (window._sugarQ = window._sugarQ || []).push(args);
  }

  markStickyShown() {
    const entity = this.sectionId || this.dataset.sectionId;
    if (!entity) return;
    if (this.getBoundingClientRect().height === 0) return;
    let first = true;
    try {
      if (sessionStorage.getItem('_sugar_imp_' + entity)) first = false;
      else sessionStorage.setItem('_sugar_imp_' + entity, '1');
    } catch (e) {}
    if (first) this.trackSticky('impression', '');
  }

  formatMoney(cents) {
    // The store's own format, the one the bar's first price (Liquid) and the cart print, in
    // every currency the page is shown in; the code goes after it the way Liquid adds it.
    const format = this.config.moneyFormat;
    const code = this.config.currencyCode;
    const withCode = this.config.showCurrencyCode && code;
    if (window.SugarPricing && typeof window.SugarPricing.formatMoney === 'function') {
      const formatted = window.SugarPricing.formatMoney(cents, format);
      return withCode ? `${formatted} ${code}` : formatted;
    }

    const amount = (parseInt(cents, 10) / 100).toFixed(2);
    if (withCode) {
      const fmtOpts = { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' };
      return `${new Intl.NumberFormat(window.Shopify?.locale || 'en-US', fmtOpts).format(amount)} ${code}`;
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }
}

// Register the custom element
if (!customElements.get('sticky-product')) {
  customElements.define('sticky-product', StickyProduct);
}
